<template>
    <div class="input-wrapper">
        <textarea
            v-if="type == 'textarea'"
            :class="['text-input', value && 'filled', { 'has-image': image }]"
            :id="name"
            :maxlength="maxLength"
            :name="name"
            :label="displayLabel"
            @change="handleChange"
            v-model="value"
            ref="inputField"
        />

        <input
            v-else
            :class="['text-input', value && 'filled', { 'has-image': image }]"
            :id="name"
            :maxlength="maxLength"
            :name="name"
            :type="type"
            :label="displayLabel"
            @change="handleChange"
            v-model="value"
            v-maska
            :data-maska="mask"
            ref="inputField"
        />

        <FloatingLabel :label="displayLabel" :icon="icon" />

        <img class="inner-image" v-if="image" :src="asset(image)" alt="" />
        <div class="error" v-if="displayErrorMessage">{{ displayErrorMessage }}</div>
    </div>
</template>

<script>
import { useField } from 'vee-validate'
import { titleCase } from '@js/utilities/language.js'
import { useStore } from 'vuex'

// lodash components
import _get from 'lodash/get'

export default {
    props: {
        customErrorMessage: String,
        icon: String,
        image: String,
        label: String,
        name: String,
        validationRules: Object,
        vuexKey: String,
        type: {
            type: String,
            default: 'text',
        },
        debounce: {
            type: Number,
            default: 250,
        },
    },
    setup(props) {
        const store = useStore()
        const { errorMessage, handleChange, resetField } = useField(props.name, props.validationRules, {
            label: titleCase(props.name),
        })

        return {
            errorMessage,
            handleChange,
            resetField,
            useField,
            store,
        }
    },
    mounted() {
        this.resetField({
            value: this.value,
        })

        this.emitter.on('inputField:resetField', () => {
            this.resetField({
                value: this.value,
            })
        })
    },
    computed: {
        value: {
            get() {
                return this.store.getters.fieldValue(this.name, this.vuexKey || 'data', !!this.vuexKey, !!this.vuexKey)
            },
            set(value) {
                if (this.vuexKey) {
                    this.store.commit('setDataFieldValue', {
                        name: this.name,
                        value: value,
                        key: this.vuexKey,
                    })
                } else {
                    this.store.dispatch('setFieldValue', {
                        name: this.name,
                        value: value,
                    })
                }

                this.$emit('input', {
                    name: this.name,
                    value: value,
                })
            },
        },
        mask() {
            return this.store.getters.field(this.name, this.vuexKey || 'data', !!this.vuexKey)?.mask ?? null
        },
        displayLabel() {
            return this.label || this.titleCase(this.name)
        },
        maxLength() {
            if (this.validationRules && this.validationRules.max) {
                return this.validationRules.max
            } else if (this.validationRules && this.validationRules.digits) {
                return this.validationRules.digits
            }

            return null
        },
        serverError() {
            return this.store.getters.fieldServerError(this.name)
        },
        displayErrorMessage() {
            return this.errorMessage ? this.interpretErrorMessage(this.errorMessage) : this.serverError
        },
    },
    methods: {
        interpretErrorMessage(errorString) {
            if (this.customErrorMessage) {
                return this.customErrorMessage
            }

            const defaultErrorStr = this.$t('errors.fieldNetworkError')

            const parts = errorString.split(' ')
            if (parts.length !== 2) {
                return defaultErrorStr
            }

            const ruleError = parts[0]
            const field = parts[1]

            if (ruleError === 'error') {
                return defaultErrorStr
            }

            let errorMsg
            switch (field) {
                case 'email':
                    errorMsg = this.$t('form.fields.emailAddress')
                    break

                case 'phone':
                    errorMsg = this.$t('form.fields.mobileNumber')
                    break

                default:
                    errorMsg = this.$t('errors.fieldGenericError')
                    break
            }

            return errorMsg
        },
        focus() {
            this.autoFocus(this.$refs.inputField)
        },
    },
}
</script>

<style lang="scss" scoped>
@import '~scss/mixins/rem';
@import '~scss/settings/responsive';

.input-wrapper {
    position: relative;

    .inner-image {
        position: absolute;
        width: 37px;
        left: 11px;
        opacity: 0;
        top: 30px;
        padding-right: 10px;
        border-right: 1px solid #c4c4c4;
        transition: 0.2s ease all;
    }

    input,
    textarea {
        &:focus,
        &.filled {
            padding-left: 10px;
            padding-top: 20px;

            + .floating-label {
                font-size: 11px;
                left: 10px;
                opacity: 1;
                top: 4px;

                + .inner-image {
                    opacity: 1;
                }

                .icon {
                    font-size: 12px;
                }

                .fa-stack {
                    font-size: 8px;

                    .icon {
                        font-size: 8px;
                    }
                }
            }

            &.has-image {
                padding-left: 55px;
            }
        }
    }

    .text-input {
        background: #ffffff;
        border-radius: 4px;
        border: 1px solid #e0e1e5;
        color: var(--input-text-color);
        font-size: 16px;
        height: 53px;
        position: relative;
        width: 100%;
    }

    textarea {
        min-height: 110px;
    }

    .floating-label {
        color: var(--input-text-color);
        font-family: var(--secondary-font);
        font-size: 16px;
        left: 10px;
        pointer-events: none;
        position: absolute;
        top: 14px;
        transition: 0.2s ease all;
        text-overflow: ellipsis;
        max-width: 300px;
        overflow: hidden;

        @media screen and (min-width: $bp-large) {
            max-width: 380px;
        }

        .icon {
            color: black;
            font-size: 20px;
            vertical-align: middle;
        }

        .fa-stack {
            color: black;
            font-size: 12px;
            vertical-align: bottom;

            .icon {
                font-size: 11px;
            }
        }
    }
}
</style>
